import { useEffect } from 'react';
import { getImageUrl } from '@/lib/image';

type SeoProps = {
  title?: string;
  description?: string;
  canonicalPath?: string;
  image?: string | null;
  keywords?: string;
  type?: 'website' | 'article' | 'product';
  robots?: string;
  structuredData?: Record<string, any> | Record<string, any>[];
};

const DEFAULT_TITLE = 'Rork Store | أنشئ متجرك الإلكتروني بسهولة';
const DEFAULT_DESCRIPTION = 'منصة Rork Store تساعدك على إنشاء وإدارة متجر إلكتروني احترافي مع المنتجات، الطلبات، التسويق، الدفع، الشحن والتحليلات.';
const DEFAULT_IMAGE = '/branding/rork-store-logo.png';

const ensureMeta = (selector: string, create: () => HTMLMetaElement) => {
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (existing) return existing;

  const tag = create();
  tag.setAttribute('data-rork-seo', 'true');
  document.head.appendChild(tag);
  return tag;
};

const setMetaName = (name: string, content: string) => {
  const tag = ensureMeta(`meta[name="${name}"]`, () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', name);
    return meta;
  });
  tag.setAttribute('content', content);
};

const setMetaProperty = (property: string, content: string) => {
  const tag = ensureMeta(`meta[property="${property}"]`, () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', property);
    return meta;
  });
  tag.setAttribute('content', content);
};

const setCanonical = (url: string) => {
  let tag = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    tag.setAttribute('data-rork-seo', 'true');
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', url);
};

const stripHtml = (value?: string | null) => (value || '')
  .replace(/<[^>]+>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const truncate = (value: string, length: number) => (
  value.length > length ? `${value.slice(0, length - 1).trim()}…` : value
);

const absoluteUrl = (path?: string | null) => {
  if (typeof window === 'undefined') return path || '';
  if (!path) return window.location.href;

  try {
    return new URL(path, window.location.origin).toString();
  } catch {
    return window.location.href;
  }
};

export function SEO({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  canonicalPath,
  image = DEFAULT_IMAGE,
  keywords,
  type = 'website',
  robots = 'index,follow',
  structuredData,
}: SeoProps) {
  useEffect(() => {
    const cleanTitle = truncate(stripHtml(title) || DEFAULT_TITLE, 70);
    const cleanDescription = truncate(stripHtml(description) || DEFAULT_DESCRIPTION, 160);
    const canonical = absoluteUrl(canonicalPath || `${window.location.pathname}${window.location.search}`);
    const imageUrl = image ? absoluteUrl(getImageUrl(image, DEFAULT_IMAGE)) : absoluteUrl(DEFAULT_IMAGE);

    document.title = cleanTitle;
    document.documentElement.lang = 'ar';
    document.documentElement.dir = 'rtl';

    setCanonical(canonical);
    setMetaName('description', cleanDescription);
    setMetaName('keywords', keywords?.trim() ? stripHtml(keywords) : '');
    setMetaName('robots', robots);
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', cleanTitle);
    setMetaName('twitter:description', cleanDescription);
    setMetaName('twitter:image', imageUrl);

    setMetaProperty('og:locale', 'ar_AR');
    setMetaProperty('og:type', type);
    setMetaProperty('og:title', cleanTitle);
    setMetaProperty('og:description', cleanDescription);
    setMetaProperty('og:url', canonical);
    setMetaProperty('og:image', imageUrl);
    setMetaProperty('og:site_name', 'Rork Store');

    document.head.querySelectorAll('script[data-rork-jsonld="true"]').forEach((node) => node.remove());
    if (structuredData) {
      const blocks = Array.isArray(structuredData) ? structuredData : [structuredData];
      blocks.filter(Boolean).forEach((block) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-rork-jsonld', 'true');
        script.textContent = JSON.stringify(block);
        document.head.appendChild(script);
      });
    }
  }, [title, description, canonicalPath, image, keywords, type, robots, structuredData]);

  return null;
}

export const seoText = {
  stripHtml,
  truncate,
  absoluteUrl,
};
